import React from "react";
import { FlaskConical, Database, Upload, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

const ENVELOPE_SAMPLE = `{
  "envelope_version": "1.0",
  "session_id":    "5e8f1a2b-...-uuid",
  "device_serial": "T-2041",
  "user_sub":      "cognito-subject-guid",
  "posted_at":     "2026-07-15T10:15:32Z",
  "ota_outcome":   "SUCCESS",

  "device_logs": [
    {
      "ts":      "2026-07-15T10:12:04Z",
      "source":  "device",
      "level":   "INFO",
      "code":    "BLE_WRITE_ACK",
      "message": "Chunk 142 acknowledged"
    }
  ],

  "app_logs": [
    {
      "ts":     "2026-07-15T10:12:04Z",
      "source": "mobile-app",
      "level":  "INFO",
      "screen": "F07a",
      "event":  "write_chunk_sent",
      "payload": { "chunk_id": 142, "role": "Clinician" }
    }
  ]
}`;

export default function LogTriagePage() {
  return (
    <div className="max-w-3xl space-y-6">
      {/* INC 2 banner */}
      <div className="flex items-center gap-4 p-5 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50">
        <FlaskConical className="h-10 w-10 text-amber-500 shrink-0" />
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-semibold text-amber-900">Log Triage — INC 2 (Future)</h2>
            <Badge variant="warning">Not Active</Badge>
          </div>
          <p className="text-sm text-amber-700">
            This page will be activated in Increment 2. Infrastructure is already provisioned in INC 1
            (S3 buckets, DynamoDB SessionIndex, Lambda stub). The POST /logs endpoint currently returns{" "}
            <code className="font-mono bg-amber-100 px-1 rounded">HTTP 503</code>.
          </p>
        </div>
      </div>

      {/* What INC 2 will deliver */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What INC 2 Will Deliver</CardTitle>
          <CardDescription>Once the Mobile team wires POST /logs and the Lambda is activated.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {[
            {
              icon: Upload,
              title: "Bundled Log Envelope Upload",
              desc: "Mobile posts one envelope per OTA event (50–500 KB) on F07a success or F07b rollback.",
            },
            {
              icon: Database,
              title: "Split Log Streams",
              desc: "Lambda Log Ingest splits device_logs[] → S3 Device Logs bucket, app_logs[] → S3 App Logs bucket. 1-year lifecycle (Standard → IA).",
            },
            {
              icon: Eye,
              title: "Session Index Query",
              desc: "DynamoDB SessionIndex binds session_id ↔ device_serial ↔ user_sub ↔ S3 object keys. Support Engineers can triage F07b rollbacks in one query.",
            },
            {
              icon: FlaskConical,
              title: "Athena + QuickSight Analytics",
              desc: "Ad-hoc queries over S3 log buckets. Support Engineer triage dashboard with timeline and error pattern views.",
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3">
              <div className="p-2 bg-slate-100 rounded-lg shrink-0">
                <Icon className="h-4 w-4 text-slate-600" />
              </div>
              <div>
                <p className="font-medium">{title}</p>
                <p className="text-slate-500 text-xs mt-0.5">{desc}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Envelope preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Envelope Contract (Preview)</CardTitle>
          <CardDescription>JSON shape that Mobile will POST to /logs on each OTA event.</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-x-auto leading-relaxed">
            <code>{ENVELOPE_SAMPLE}</code>
          </pre>
        </CardContent>
      </Card>

      {/* INC 1 infra already provisioned */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">INC 1 Infrastructure Already Provisioned</CardTitle>
          <CardDescription>Created now so INC 2 requires no re-architecture.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            {[
              { name: "S3 Device Logs bucket", status: "Empty · 1-year lifecycle configured", ok: true },
              { name: "S3 App Logs bucket", status: "Empty · 1-year lifecycle configured", ok: true },
              { name: "DynamoDB SessionIndex table", status: "Empty · PK: device_serial + timestamp", ok: true },
              { name: "Lambda Log Ingest (stub)", status: "Deployed · returns HTTP 503", ok: false },
              { name: "Athena + Glue + QuickSight", status: "Not deployed — deferred entirely to INC 2", ok: false },
            ].map((item) => (
              <div key={item.name} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <p className="font-medium">{item.name}</p>
                  <p className="text-xs text-slate-400">{item.status}</p>
                </div>
                <Badge variant={item.ok ? "success" : "warning"}>
                  {item.ok ? "Provisioned" : "Dormant / Deferred"}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Activation checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Path to Activation</CardTitle>
          <CardDescription>Steps needed in INC 2 — no INC 1 services need to change.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm list-decimal list-inside text-slate-700">
            <li>Mobile team implements bundling logic and wires POST /logs on F07a / F07b paths.</li>
            <li>Backend activates Lambda Log Ingest body — split envelope, write S3, update SessionIndex.</li>
            <li>This page wires to GET /admin/logs/sessions?serial=&amp;ts= to surface session query.</li>
            <li>Stand up Athena + Glue Catalog + QuickSight once first month of log data has accumulated.</li>
            <li>Privacy review: decide whether app_logs (clinician-bound) should have 90-day instead of 1-year retention.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
